import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Chip,
  Drawer,
  Group,
  NumberInput,
  ScrollArea,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Activity, RefreshCw, Zap } from 'react-feather';
import { env } from '../../../../config/env';
import { useAuthStore } from '../../../../stores/auth';
import {
  sessionDebugApi,
  type ActiveSessionParticipant,
  type DebugTraceEvent,
  type InjectTranscriptLine,
  type ReadTheRoomTraceEntry,
  type InferenceDetailEntry,
} from '../../../../services/api/coachActiveSession';
import { getApiErrorMessage } from '../../../../services/api/client';

/**
 * MED-1105 — dev-only debug drawer for the entire in-session AI pipeline.
 *
 * Unified, time-ordered timeline of: the medley-api bot-join dispatch hops
 * (fetched over REST), and the live session-intelligence stream over the socket
 * — bot lifecycle, transcript lines, the AI's per-window `inference`
 * classifications, and the resulting nudges. Plus a Force Join button and a
 * Testing section to trigger nudges/insights on demand.
 *
 * Gating: the parent only mounts this when `?debug=1`. Endpoints require auth;
 * force-join is non-prod only.
 */
export interface SessionDebugDrawerProps {
  sessionId: string | undefined;
  /** Unified live debug stream from the in-session socket (useSessionNudges). */
  debugEvents: DebugTraceEvent[];
  /** How many transcript blocks the AI actually analyzed vs debounced (skipped). */
  inferenceStats: { analyses: number; debounced: number };
  /** Whether the in-session socket is connected (drives the live-stream hint). */
  isConnected: boolean;
  /**
   * MED-1210 — read-the-room diagnostic traces (why "Generate Recommendation"
   * did/didn't surface a prompt). Passed only for the session leader (the sole
   * recipient — SI emits point-to-point); omitted/empty otherwise.
   */
  readTheRoomTraces?: ReadTheRoomTraceEntry[];
  /**
   * MED-1245 — automatic-nudge rationale details (why the AI nudged or stayed
   * silent, and why a would-be nudge did/didn't surface). Passed only for the
   * session leader (the sole recipient — SI emits to the leaders room); omitted
   * for a non-leader viewer.
   */
  inferenceDetails?: InferenceDetailEntry[];
  /**
   * MED-1281 — the session's real participants, used to populate the transcript
   * injector's speaker selector. Speaker names matter: the AI's airtime split
   * keys on the raw speaker string, so a name that doesn't match what the
   * meeting provider emits creates a phantom speaker in the window.
   */
  participants?: ActiveSessionParticipant[];
  /**
   * MED-1281 — whether this viewer may inject transcript. Mirrors the
   * pause/resume-AI leader gate; client-side hiding only, medley-api's
   * per-user check and session-intelligence's env flag are authoritative.
   */
  canInject?: boolean;
  /**
   * MED-1281 — ms epoch of the last COMPLETED analysis, used to estimate when
   * the shared 30s inference debounce next lets a model call through. It lands
   * after the model round-trip (the server-side debounce stamp is set before
   * it), so the estimate is conservative — it under-promises eligibility.
   */
  lastCheckAt?: number | null;
}

/** MED-1210 — per-cause label + color for a read-the-room trace row. */
const RTR_CAUSE_META: Record<
  ReadTheRoomTraceEntry['cause'],
  { label: string; color: string }
> = {
  ok: { label: 'surfaced a prompt', color: 'teal' },
  paused: { label: 'AI paused', color: 'gray' },
  insufficient_transcript: { label: 'not enough transcript', color: 'yellow' },
  no_confident_read: { label: 'model: quiet / low-confidence', color: 'yellow' },
  nothing_new: { label: 'covered — every thread already pulled', color: 'yellow' },
  // MED-1273 — the read existed but named someone in the spoken prompt in a
  // position the guard could not repair, so it was dropped rather than shipped.
  name_unrepairable: { label: 'refused: named a participant aloud', color: 'orange' },
  no_active_session: { label: 'no active session', color: 'red' },
  not_primed: { label: 'recipe not primed', color: 'red' },
  normalize_error: { label: 'context assembly failed', color: 'red' },
  model_timeout_or_empty: { label: 'model timeout / empty', color: 'red' },
};

/** A cause this build doesn't know about must not blank the debug pane — SI can
 *  ship a new one before the FE does (`nothing_new` shipped that way and made
 *  `meta.color` throw on the row). Render it literally instead. */
function causeMeta(cause: ReadTheRoomTraceEntry['cause']): { label: string; color: string } {
  return RTR_CAUSE_META[cause] ?? { label: cause, color: 'gray' };
}

/**
 * MED-1210 — one read-the-room trace row: cause + one-liner always visible;
 * the full request (recipe + system prompt + context), the raw model response,
 * and the judge verdict are collapsed behind an expander (AC3/AC4/AC5).
 */
function ReadTheRoomTraceRow({ trace }: { trace: ReadTheRoomTraceEntry }) {
  const [open, setOpen] = useState(false);
  const meta = causeMeta(trace.cause);
  return (
    <Box style={{ borderLeft: `3px solid var(--mantine-color-${meta.color}-6)`, paddingLeft: 8, paddingTop: 3, paddingBottom: 3 }}>
      <Group gap={6} wrap="nowrap" align="center">
        <Badge size="xs" color={meta.color} variant="light">{trace.outcome}</Badge>
        <Text size="xs" fw={600}>{meta.label}</Text>
        <Text size="xs" c="dimmed" ff="monospace">{trace.latencyMs}ms</Text>
        <Text size="xs" c="dimmed" ml="auto">{formatTime(trace.ts)}</Text>
        <ActionIcon size="sm" variant="subtle" color="gray" aria-label={open ? 'Collapse trace' : 'Expand trace'} onClick={() => setOpen(o => !o)}>
          <Text size="xs">{open ? '−' : '+'}</Text>
        </ActionIcon>
      </Group>
      <Text size="sm" style={{ wordBreak: 'break-word' }}>{trace.detail}</Text>
      {open && (
        <Stack gap={6} mt={6}>
          {trace.window && (
            <Text size="xs" c="dimmed" ff="monospace">window: {trace.window.lines} lines · {trace.window.chars} chars</Text>
          )}
          {trace.request && (
            <Box>
              <Text size="xs" fw={700} c="dimmed">REQUEST</Text>
              <Text size="xs" c="dimmed" ff="monospace">
                {trace.request.recipe.name}@{trace.request.recipe.version} · {trace.request.recipe.model} · temp {trace.request.recipe.temperature} · maxTokens {trace.request.recipe.maxTokens}
              </Text>
              <details>
                <summary style={{ cursor: 'pointer', fontSize: 12 }}>system prompt</summary>
                <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 11, margin: '4px 0', maxHeight: 200, overflow: 'auto' }}>{trace.request.systemPrompt}</pre>
              </details>
              <details>
                <summary style={{ cursor: 'pointer', fontSize: 12 }}>context sent to model</summary>
                <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 11, margin: '4px 0', maxHeight: 280, overflow: 'auto' }}>{JSON.stringify(trace.request.context, null, 2)}</pre>
              </details>
            </Box>
          )}
          {trace.response && (
            <Box>
              <Text size="xs" fw={700} c="dimmed">RESPONSE</Text>
              <Text size="xs" ff="monospace">decision: {trace.response.decision} · confidence: {trace.response.decision_confidence}</Text>
              {/* v6 — `why` is the model's own rationale, not a verified account of
                  context: a 2026-07-30 staging trace asserted a prior nudge "surfaced
                  the drift" when the nudge said no such thing. Label it so readers
                  cross-check claims against "context sent to model" above. */}
              {trace.response.why && <Text size="xs"><strong>why (model rationale — unverified):</strong> {trace.response.why}</Text>}
              {trace.response.noticing && <Text size="xs"><strong>noticing:</strong> {trace.response.noticing}</Text>}
              {trace.response.suggested_prompt && <Text size="xs"><strong>prompt:</strong> {trace.response.suggested_prompt}</Text>}
            </Box>
          )}
          <Box>
            <Text size="xs" fw={700} c="dimmed">EVAL</Text>
            {trace.judge ? (
              <>
                <Text size="xs" ff="monospace">judge score: {trace.judge.score} · {trace.judge.critical_issues.length} critical issue(s)</Text>
                {trace.judge.critical_issues.map((iss, i) => <Text key={i} size="xs" c="red">• {iss}</Text>)}
                {trace.judge.narrative && <Text size="xs" c="dimmed">{trace.judge.narrative}</Text>}
              </>
            ) : (
              <Text size="xs" c="dimmed">{trace.judgeSampled ? 'evaluating… (verdict pending)' : 'not evaluated (sampled out)'}</Text>
            )}
          </Box>
        </Stack>
      )}
    </Box>
  );
}

/** MED-1245 — per-outcome label + color for an automatic-nudge detail row. */
const INFERENCE_OUTCOME_META: Record<
  InferenceDetailEntry['outcome'],
  { label: string; color: string }
> = {
  surfaced: { label: 'nudged the room', color: 'teal' },
  none: { label: 'stayed silent (model chose none)', color: 'gray' },
  suppressed_cooldown: { label: 'suppressed — cooldown', color: 'yellow' },
  suppressed_empty: { label: 'suppressed — empty message', color: 'yellow' },
};

/**
 * MED-1245 — one automatic-nudge detail row: outcome + confidence always
 * visible; the model's `why` (and message, if any) collapsed behind an expander.
 * This is what answers "why did the automatic AI stay silent / not surface?".
 */
function InferenceDetailRow({ detail }: { detail: InferenceDetailEntry }) {
  const [open, setOpen] = useState(false);
  const meta = INFERENCE_OUTCOME_META[detail.outcome];
  return (
    <Box style={{ borderLeft: `3px solid var(--mantine-color-${meta.color}-6)`, paddingLeft: 8, paddingTop: 3, paddingBottom: 3 }}>
      <Group gap={6} wrap="nowrap" align="center">
        <Badge size="xs" color={meta.color} variant="light">{detail.decision}</Badge>
        <Text size="xs" fw={600}>{meta.label}</Text>
        <Text size="xs" c="dimmed" ff="monospace">conf {detail.decisionConfidence}</Text>
        {detail.nextEligibleInSec != null && (
          <Text size="xs" c="dimmed" ff="monospace">next in {detail.nextEligibleInSec}s</Text>
        )}
        <Text size="xs" c="dimmed" ml="auto">{formatTime(detail.ts)}</Text>
        <ActionIcon size="sm" variant="subtle" color="gray" aria-label={open ? 'Collapse detail' : 'Expand detail'} onClick={() => setOpen(o => !o)}>
          <Text size="xs">{open ? '−' : '+'}</Text>
        </ActionIcon>
      </Group>
      {open && (
        <Stack gap={4} mt={6}>
          <Text size="xs" c="dimmed" ff="monospace">{detail.nudgeType}</Text>
          {/* v6 — same unverified-rationale label as the read-the-room rows above. */}
          {detail.why
            ? <Text size="xs"><strong>why (model rationale — unverified):</strong> {detail.why}</Text>
            : <Text size="xs" c="dimmed">why: (model returned none)</Text>}
          {detail.message && <Text size="xs"><strong>message:</strong> {detail.message}</Text>}
        </Stack>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// MED-1281 — transcript injection
// ---------------------------------------------------------------------------

/**
 * Mirrors DEBOUNCE_SECONDS in session-intelligence (src/config/index.ts): the
 * per-session floor between two real model calls. Nothing exposes it over the
 * wire, so it is duplicated here by hand — if it changes there, change it here.
 */
const INFERENCE_DEBOUNCE_SECONDS = 30;

/**
 * The AI reads a rolling 5-minute window (TRANSCRIPT_WINDOW_MINUTES). Simulated
 * silence works by back-dating the PREVIOUS line, so a gap at or beyond the
 * window evicts that line entirely and the gap silently collapses to zero. Warn
 * well before the cliff rather than letting the feature defeat itself.
 */
const MAX_USEFUL_GAP_SECONDS = 240;

/** Contract limit: 1..50 lines per call. */
const MAX_INJECT_LINES = 50;

/** Sentinel for "type a speaker name that isn't in the participant list". */
const CUSTOM_SPEAKER = '__custom__';

/**
 * What each failure status actually MEANS here — keyed by status because the
 * server's own message ("Not found") cannot distinguish the causes and this
 * pane exists precisely to tell them apart. `0` is the catch-all (offline,
 * timeout, unparseable).
 */
const INJECT_FAILURE_HINT: Record<number, string> = {
  0: 'Injection failed.',
  400: 'Rejected as malformed — check speaker/text are non-empty and the gap is 0-600s.',
  401: 'Not signed in — the session expired; reload the page.',
  403: 'Not allowed: either you do not lead/coach this session, or this is production (injection is blocked there).',
  404: 'Nothing to inject into: the session is not active, ALLOW_TRANSCRIPT_INJECTION is not set on this environment, or live session delivery is off for this cohort.',
  409: 'AI listening is paused for this session — resume it first (the pipeline drops all transcript while paused).',
  502: 'session-intelligence is unreachable from medley-api.',
  503: 'medley-api has no SESSION_INTELLIGENCE_URL configured.',
};

/**
 * `Speaker: text` prefix. Deliberately conservative (short label, single colon)
 * rather than a real parser — a body that legitimately starts with `word:` will
 * mis-split, which is exactly why the parse result is previewed under the
 * textarea instead of being trusted silently.
 */
const SPEAKER_PREFIX_RE = /^([^:]{1,40}):\s*(.+)$/;

function parseBlob(blob: string, fallbackSpeaker: string): InjectTranscriptLine[] {
  return blob
    .split('\n')
    .map(l => l.trim())
    .filter(Boolean)
    .map(l => {
      const m = SPEAKER_PREFIX_RE.exec(l);
      return m
        ? { speaker: m[1].trim(), text: m[2].trim() }
        : { speaker: fallbackSpeaker, text: l };
    });
}

interface InjectPreset {
  key: string;
  label: string;
  /** The REAL eligibility condition this blob is built to satisfy. */
  hint: string;
  lines: InjectTranscriptLine[];
}

/**
 * Presets authored from the actual trigger conditions in the SI pipeline
 * (`eligibleTriggers`), not from imagination. Each one is the cheapest input
 * that makes its dimension ELIGIBLE — whether the model then nudges is the
 * thing under test, and `pickTrigger` round-robins, so the dimension you seeded
 * may not be the one asked about on the very next tick (watch `nudgeType` under
 * Automatic reads and expect up to ~4 ticks).
 *
 * Speaker names come from the live participant list where possible: airtime is
 * bucketed by raw speaker string, so borrowed names keep the injected window
 * consistent with the real one.
 */
function buildPresets(names: string[]): InjectPreset[] {
  const a = names[0] ?? 'Alex';
  const b = names[1] ?? names[0] ?? 'Sam';
  return [
    {
      key: 'topic_drift',
      label: 'Topic drift',
      hint: 'Off-topic small talk across two voices. topic_drift is eligible in every authored session (it has a guide/topic), so the only variable is whether the content is genuinely off the anchor — this is.',
      lines: [
        { speaker: a, text: 'honestly the parking at the stadium on saturday was a nightmare, we circled that lot for forty minutes' },
        { speaker: b, text: 'ha, and the tailgate deck was full before noon, my cousin got there at ten and still parked six blocks out' },
        { speaker: a, text: 'he swears by the shuttle from the transit deck but the last one back leaves at ten thirty so you miss the end' },
        { speaker: b, text: 'did you see the forecast though, they are calling for rain the entire weekend' },
        { speaker: a, text: 'i ordered one of those pop up canopies online, we will find out saturday whether it survives the wind' },
      ],
    },
    {
      // ~2300 characters from ONE speaker. Airtime is estimated from character
      // count at ~15 chars/sec, so this is ~155 seconds of talk — enough to
      // clear 60% of a 5-minute window unless the room has been busy. A short
      // blob will NOT tip the split, however lopsided it reads.
      key: 'rebalance_speaking',
      label: 'Rebalance speaking',
      hint: `A single-voice monologue of ~2,300 characters (~155s of estimated airtime at 15 chars/sec). rebalance_speaking needs one voice past 60% of the 5-minute window, so if the room has been talking you may need to send it twice.`,
      lines: [
        { speaker: a, text: 'so the thing i keep coming back to is that when we reorganized the team in january i inherited four people who had never worked together and i think i underestimated how much of my week would go into just translating between them, because each one came from a different part of the business and they all have completely different assumptions about what good looks like, and i did not build any time for that translation work into what i committed to for the quarter' },
        { speaker: a, text: 'and then what happened is the first two sprints slipped, not badly, maybe three days each, but my director started showing up in our standups, which the team read as a lack of confidence, and honestly i read it that way too, so i started over preparing for those standups, which took another four or five hours a week out of exactly the time i was supposed to be spending on the translation problem i just described' },
        { speaker: a, text: 'i tried a few things, i wrote a one page charter for the team, i set up skip levels so people could talk to my director directly instead of through me, i even moved planning to mondays so the week starts with alignment instead of ending with it, and some of that helped at the margins but none of it touched the underlying thing, which is that i am the only shared context these four people have' },
        { speaker: a, text: 'what i have not done, and i think this is the part i am avoiding, is go back to my director and say the commitment we made in january was built on an assumption that turned out to be wrong, because that conversation feels like admitting i cannot run the team, even though rationally i know that is not what it is, and every week i do not have it the number gets bigger and harder to explain' },
        { speaker: a, text: 'so that is where i am, i am spending my energy managing the perception of the problem instead of the problem, and i can feel myself starting to do the same thing with the team, telling them everything is fine when they can obviously see that it is not, which is exactly the pattern i said i wanted to break when we started this program' },
        { speaker: a, text: 'and the honest version is that i have known this for about six weeks, i wrote it in my notes after our last session and then did nothing with it, because the moment i say it out loud to my director it becomes real and i lose the option of quietly fixing it before anyone notices, which i think we both know was never actually an option' },
      ],
    },
    {
      // The ONLY preset where line ordering decides whether the model ever sees
      // the signal, because two pipeline facts collide:
      //
      //  • silence is the gap between the last two BUFFERED lines, so a lone
      //    line into an empty window measures zero — an anchor is required;
      //  • every line drives its own tick, but the shared 30s debounce is
      //    consumed in the continuation, so the FIRST line's tick is the one
      //    that reaches the model. A gap carried only on the tail is measured
      //    correctly and then debounced away.
      //
      // So the gap goes on BOTH lines. In a live session (window already has
      // real speech) line 1 back-dates that real line and the winning tick sees
      // 30s — one click, done. From cold there is nothing to back-date, line 1
      // measures 0 and the lull only lands on line 2's debounced tick; that is
      // what "Re-trigger analysis" is for, and 30s of real wall-clock between
      // the blob and the re-trigger line reproduces the gap on its own.
      key: 'reflect_silence',
      label: 'Reflect on silence',
      hint: 'An anchor plus a short line, both carrying 30s of simulated silence (the >=25s bar). If the room has already been talking this lands on the very first tick. From a cold/empty window there is nothing to back-date, so wait out the countdown and hit "Re-trigger analysis" — that tick carries the lull.',
      lines: [
        { speaker: a, text: 'so yeah, that is kind of where i landed with the whole thing.', gapSeconds: 30 },
        { speaker: b, text: 'yeah.', gapSeconds: 30 },
      ],
    },
    {
      key: 'check_understanding',
      label: 'Missed disclosure',
      hint: 'An on-topic window carrying ONE concrete, unexplored disclosure the group walks straight past. check_understanding is always eligible, but the recipe returns none unless it can quote a specific moment — this gives it one.',
      lines: [
        { speaker: a, text: 'the rollout went about how we expected, we hit the date and support tickets were actually down week over week' },
        { speaker: b, text: 'that is good to hear, anything you would change about the sequencing next time' },
        { speaker: a, text: 'probably the training, we ran it two weeks before go live and by launch nobody remembered any of it' },
        { speaker: b, text: 'makes sense, we should carry that into the next one' },
        { speaker: a, text: 'yeah. i also let go of my two most senior engineers the friday before launch and i have not really talked to anyone about that' },
        { speaker: b, text: 'okay. so for the next rollout, do we move the training closer to the date or split it' },
      ],
    },
  ];
}

/**
 * MED-1281 — inject synthetic transcript into a LIVE session.
 *
 * Text sent here enters at the same callback the Recall webhook drives, so it
 * is indistinguishable from speech to everything downstream: rolling window →
 * trigger selection → recipe → sampled judge → cooldown → nudge. That is the
 * point — anything that shortcuts further down would not test the behaviour.
 *
 * Two things about the pipeline shape the UI, and both are load-bearing:
 *
 * 1. Per-line context is captured SYNCHRONOUSLY but the shared 30s debounce is
 *    consumed in the continuation, so a multi-line blob is analyzed as of its
 *    FIRST line. The blob still lands in the 5-minute rolling window, so the
 *    way to get the model a context containing all of it is a second, tiny
 *    injection once the debounce clears — hence "Re-trigger analysis (Xs)"
 *    rather than a disabled button. The countdown is the mechanism, not a
 *    warning label.
 * 2. A debounced tick and a failed model call both surface as "debounced" on
 *    the FE, so a ticking debounce counter is not proof the feature worked.
 */
function TranscriptInjector({
  sessionId,
  participants,
  lastCheckAt,
}: {
  sessionId: string | undefined;
  participants: ActiveSessionParticipant[];
  lastCheckAt: number | null;
}) {
  const speakerOptions = useMemo(() => {
    const names = Array.from(
      new Set(participants.map(p => p.display_name).filter(Boolean))
    );
    return [
      ...names.map(n => ({ value: n, label: n })),
      { value: CUSTOM_SPEAKER, label: 'Other (type a name)…' },
    ];
  }, [participants]);

  const [speaker, setSpeaker] = useState<string>(CUSTOM_SPEAKER);
  const [customSpeaker, setCustomSpeaker] = useState('');
  const [blob, setBlob] = useState('');
  const [gapSeconds, setGapSeconds] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  // Seed the selector with the first real participant once the roster arrives
  // (it loads after first paint). Guarded by a ref, not by the current value:
  // the participants array is re-fetched periodically, and a value-based guard
  // would yank the operator off "Other" mid-keystroke on every refresh.
  const speakerSeeded = useRef(false);
  useEffect(() => {
    if (speakerSeeded.current || speakerOptions.length <= 1) return;
    speakerSeeded.current = true;
    setSpeaker(speakerOptions[0].value);
  }, [speakerOptions]);

  // 1s tick for the debounce countdown. The drawer unmounts its body when
  // closed, so this only runs while the pane is actually open.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const debounceLeft =
    lastCheckAt == null
      ? 0
      : Math.max(
          0,
          Math.ceil(
            (lastCheckAt + INFERENCE_DEBOUNCE_SECONDS * 1000 - now) / 1000
          )
        );

  const effectiveSpeaker =
    speaker === CUSTOM_SPEAKER ? customSpeaker.trim() : speaker;
  const parsed = useMemo(
    () => parseBlob(blob, effectiveSpeaker),
    [blob, effectiveSpeaker]
  );
  const presets = useMemo(
    () => buildPresets(participants.map(p => p.display_name).filter(Boolean)),
    [participants]
  );

  const blobError =
    parsed.length === 0
      ? null
      : parsed.length > MAX_INJECT_LINES
        ? `Too many lines (${parsed.length}); the endpoint accepts ${MAX_INJECT_LINES}.`
        : parsed.some(l => !l.speaker)
          ? 'Some lines have no speaker — pick one above or prefix them with "Name: ".'
          : null;

  const send = useCallback(
    async (lines: InjectTranscriptLine[], label: string) => {
      if (!sessionId || lines.length === 0) return;
      setBusy(label);
      setResult(null);
      try {
        const res = await sessionDebugApi.injectTranscript(sessionId, lines);
        setResult({
          ok: true,
          text: `${res.injected} line${res.injected === 1 ? '' : 's'} injected. Watch for a new row under Automatic reads — if the debounced counter ticks instead, the 30s window had not elapsed (or the model call failed; both report as debounced).`,
        });
      } catch (err) {
        // The server ALWAYS sends an `error` string (both medley-api and
        // session-intelligence use { success:false, error }), and
        // getApiErrorMessage prefers that over the fallback — so a fallback
        // alone would never render. The bare message ("Not found") is also the
        // least useful thing to show here, because the whole point of this tool
        // is telling "flag off" apart from "session over" apart from "paused".
        // So the status is what selects the explanation, and the server message
        // rides along as the raw detail.
        const status = (err as { status?: number } | null)?.status;
        setResult({
          ok: false,
          text: `${INJECT_FAILURE_HINT[status ?? 0] ?? INJECT_FAILURE_HINT[0]} (${getApiErrorMessage(err, 'request failed')})`,
        });
      } finally {
        setBusy(null);
      }
    },
    [sessionId]
  );

  const runPreset = (p: InjectPreset) => {
    // Mirror what is going out into the composer so the operator can see it,
    // tweak it and re-send — the preset is a one-click path, not a black box.
    setBlob(p.lines.map(l => `${l.speaker}: ${l.text}`).join('\n'));
    // Carry the preset's gap into the control so a follow-up Re-trigger keeps
    // the simulated lull alive instead of erasing it with a fresh line.
    setGapSeconds(p.lines.reduce((m, l) => Math.max(m, l.gapSeconds ?? 0), 0));
    void send(p.lines, p.key);
  };

  const injectBlob = () =>
    void send(
      // Per the contract the "silence before this blob" control lands on the
      // FIRST line: SI back-dates whatever preceded it, putting the lull ahead
      // of the blob rather than inside it.
      parsed.map((l, i) =>
        i === 0 && gapSeconds > 0 ? { ...l, gapSeconds } : l
      ),
      'blob'
    );

  const reTrigger = () =>
    void send(
      [
        {
          speaker: effectiveSpeaker || 'Coach',
          text: 'mm-hmm.',
          gapSeconds: gapSeconds > 0 ? gapSeconds : undefined,
        },
      ],
      'retrigger'
    );

  return (
    <Box mt={10} pt={10} style={{ borderTop: '1px dashed #e3c9ff' }}>
      <Text size="xs" fw={700} c="grape">
        Inject transcript
      </Text>
      <Text size="9px" c="dimmed" mt={2}>
        Pushes text in at the same place the meeting bot delivers speech, so it runs the whole chain
        (window → trigger → recipe → judge → cooldown → nudge). Staging only — the endpoint does not
        exist in production. Injected lines are NOT written to the persisted session transcript.
      </Text>

      {/* Presets — authored from the real trigger thresholds. */}
      <Group gap={6} mt={8}>
        {presets.map(p => (
          <Tooltip key={p.key} label={p.hint} multiline w={320} withArrow position="bottom">
            <Button
              size="compact-xs"
              variant="light"
              color="grape"
              loading={busy === p.key}
              disabled={!sessionId || (busy !== null && busy !== p.key)}
              onClick={() => runPreset(p)}
            >
              {p.label}
            </Button>
          </Tooltip>
        ))}
      </Group>

      <Group gap="xs" mt={8} align="flex-start" wrap="nowrap">
        <Select
          size="xs"
          label="Speaker"
          data={speakerOptions}
          value={speaker}
          onChange={v => setSpeaker(v ?? CUSTOM_SPEAKER)}
          allowDeselect={false}
          style={{ flex: 1 }}
        />
        {speaker === CUSTOM_SPEAKER && (
          <TextInput
            size="xs"
            label="Name"
            placeholder="e.g. Brook"
            value={customSpeaker}
            onChange={e => setCustomSpeaker(e.currentTarget.value)}
            style={{ flex: 1 }}
          />
        )}
        <NumberInput
          size="xs"
          label="Silence before (s)"
          min={0}
          max={600}
          value={gapSeconds}
          onChange={v => setGapSeconds(typeof v === 'number' ? v : 0)}
          style={{ width: 120 }}
        />
      </Group>
      {gapSeconds > 0 && gapSeconds <= MAX_USEFUL_GAP_SECONDS && (
        <Text size="9px" c="dimmed" mt={2}>
          Applied to the FIRST line of the blob: the previously buffered line gets back-dated by{' '}
          {gapSeconds}s. Needs at least one line already in the window to measure against — the
          Reflect-on-silence preset sends its own anchor so it works from cold.
        </Text>
      )}
      {gapSeconds > MAX_USEFUL_GAP_SECONDS && (
        <Text size="9px" c="orange" mt={2}>
          Above ~{MAX_USEFUL_GAP_SECONDS}s the back-dated line falls out of the 5-minute window and the
          simulated silence collapses to zero. Use 30.
        </Text>
      )}

      <Textarea
        size="xs"
        mt={6}
        autosize
        minRows={3}
        maxRows={8}
        placeholder={'One transcript line per row. Optional per-line prefix:\nBrook: so anyway, the parking at the stadium is a nightmare'}
        value={blob}
        onChange={e => setBlob(e.currentTarget.value)}
      />

      {/* Parsed preview — the prefix rule is deliberately naive, so show the
          attribution it produced BEFORE anything is sent. */}
      {parsed.length > 0 && (
        <Box mt={4} style={{ maxHeight: 110, overflow: 'auto' }}>
          {parsed.slice(0, 12).map((l, i) => (
            <Text key={i} size="9px" c="dimmed" ff="monospace" style={{ wordBreak: 'break-word' }}>
              <strong>{l.speaker || '(no speaker)'}</strong> · {l.text.slice(0, 90)}
              {l.text.length > 90 ? '…' : ''}
            </Text>
          ))}
          {parsed.length > 12 && (
            <Text size="9px" c="dimmed">…and {parsed.length - 12} more</Text>
          )}
        </Box>
      )}
      {blobError && (
        <Text size="9px" c="red" mt={2}>
          {blobError}
        </Text>
      )}

      <Group gap="xs" mt={8}>
        <Button
          size="xs"
          color="grape"
          loading={busy === 'blob'}
          disabled={!sessionId || parsed.length === 0 || blobError !== null || busy !== null}
          onClick={injectBlob}
        >
          Inject {parsed.length || 0} line{parsed.length === 1 ? '' : 's'}
        </Button>
        <Tooltip
          label="Sends one short line so the model gets a tick whose window already holds the whole blob — a blob is otherwise analyzed as of its first line."
          multiline
          w={300}
          withArrow
        >
          <Button
            size="xs"
            variant="light"
            color="indigo"
            loading={busy === 'retrigger'}
            disabled={!sessionId || debounceLeft > 0 || busy !== null}
            onClick={reTrigger}
          >
            {debounceLeft > 0
              ? `Re-trigger analysis (${debounceLeft}s)`
              : 'Re-trigger analysis'}
          </Button>
        </Tooltip>
      </Group>

      {result && (
        <Text size="9px" c={result.ok ? 'teal' : 'red'} mt={4}>
          {result.text}
        </Text>
      )}
    </Box>
  );
}

const STATUS_COLOR: Record<DebugTraceEvent['status'], string> = {
  ok: 'teal',
  skip: 'gray',
  error: 'red',
};

const SOURCE_LABEL: Record<DebugTraceEvent['source'], string> = {
  'medley-api': 'api',
  'session-intelligence': 'si',
  recall: 'recall',
};

type Category = 'botjoin' | 'transcript' | 'inference' | 'nudge';
const CATEGORIES: { key: Category; label: string }[] = [
  { key: 'botjoin', label: 'Bot-join' },
  { key: 'transcript', label: 'Transcript' },
  { key: 'inference', label: 'Inference' },
  { key: 'nudge', label: 'Nudges' },
];

function categoryOf(e: DebugTraceEvent): Category {
  if (e.hop === 'transcript') return 'transcript';
  if (e.hop === 'inference') return 'inference';
  if (e.hop.startsWith('nudge')) return 'nudge';
  return 'botjoin'; // dispatch.* / force_rejoin / si.received / recall.* / join.outcome
}

const canForceJoin = env.environment !== 'production';

function formatTime(ts: string): string {
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? ts : d.toLocaleTimeString();
}

export function SessionDebugDrawer({ sessionId, debugEvents, inferenceStats, isConnected, readTheRoomTraces, inferenceDetails, participants = [], canInject = false, lastCheckAt = null }: SessionDebugDrawerProps) {
  const token = useAuthStore((s) => s.accessToken);
  const [opened, setOpened] = useState(false);
  const [dispatchTrace, setDispatchTrace] = useState<DebugTraceEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [forcing, setForcing] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [active, setActive] = useState<Category[]>(['botjoin', 'transcript', 'inference', 'nudge']);

  const loadDispatchTrace = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      setDispatchTrace(await sessionDebugApi.getDispatchTrace(sessionId));
    } catch (err) {
      notifications.show({ color: 'red', title: 'Debug trace', message: getApiErrorMessage(err, 'Failed to load dispatch trace') });
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (opened) void loadDispatchTrace();
  }, [opened, loadDispatchTrace]);

  const forceRejoin = useCallback(async () => {
    if (!sessionId) return;
    setForcing(true);
    try {
      setDispatchTrace(await sessionDebugApi.forceRejoin(sessionId));
      notifications.show({ color: 'blue', title: 'Force join', message: 'Re-dispatched the bot join — watch the trace below.' });
    } catch (err) {
      notifications.show({ color: 'red', title: 'Force join failed', message: getApiErrorMessage(err, 'Force join failed') });
    } finally {
      setForcing(false);
    }
  }, [sessionId]);

  // Testing aids — call session-intelligence directly (same origin the socket
  // uses; auth'd with the user token). Results stream back over the socket and
  // appear in the timeline below.
  const siPost = useCallback(
    async (path: string, body: object, label: string, okMsg: string) => {
      if (!sessionId) return;
      setBusy(label);
      try {
        const res = await fetch(`${env.sessionIntelligenceUrl}${path}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error(`session-intelligence responded ${res.status}`);
        notifications.show({ color: 'grape', title: 'Testing', message: okMsg });
      } catch (err) {
        notifications.show({ color: 'red', title: 'Testing', message: getApiErrorMessage(err, `${label} failed`) });
      } finally {
        setBusy(null);
      }
    },
    [sessionId, token],
  );


  const replayScenario = () =>
    siPost('/api/sessions/demo', { sessionId, scenario: 'drift-then-bridge' }, 'replay',
      'Replaying a drift scenario through the real AI pipeline — transcript → inference → nudge will stream in.');

  // At-a-glance health: is audio flowing, is the AI analyzing, are nudges firing?
  // Also surface the topic anchor — without it topic-drift nudges can never fire.
  const counts = useMemo(() => {
    let transcript = 0;
    let nudge = 0;
    let anchor: { ok: boolean; detail: string } | null = null;
    for (const e of debugEvents) {
      if (e.hop === 'transcript') transcript += 1;
      else if (e.hop.startsWith('nudge')) nudge += 1;
      else if (e.hop === 'topic_anchor') anchor = { ok: e.status === 'ok', detail: e.detail };
    }
    return { transcript, nudge, anchor };
  }, [debugEvents]);

  // One chronological timeline: medley-api dispatch hops + live SI/Recall stream.
  const timeline = useMemo(() => {
    const activeSet = new Set(active);
    return [...dispatchTrace, ...debugEvents]
      .filter((e) => activeSet.has(categoryOf(e)))
      .sort((a, b) => a.ts.localeCompare(b.ts));
  }, [dispatchTrace, debugEvents, active]);

  return (
    <>
      <Tooltip label="Session debug (dev)" position="left">
        <ActionIcon
          variant="filled" color="grape" size="lg" radius="xl"
          onClick={() => setOpened(true)}
          aria-label="Open session debug drawer"
          data-testid="session-debug-fab"
          style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 350 }}
        >
          <Activity size={18} />
        </ActionIcon>
      </Tooltip>

      <Drawer
        opened={opened}
        onClose={() => setOpened(false)}
        position="right"
        size="lg"
        title={
          <Group gap="xs">
            <Text fw={700}>Session debug</Text>
            <Badge size="xs" color="grape" variant="light">dev</Badge>
            <Badge size="xs" variant="dot" color={isConnected ? 'teal' : 'gray'}>
              socket {isConnected ? 'live' : 'offline'}
            </Badge>
          </Group>
        }
        data-testid="session-debug-drawer"
      >
        <Stack gap="sm">
          {/* Testing aids */}
          <Box style={{ background: '#faf5ff', border: '1px solid #efe0ff', borderRadius: 8, padding: 10 }}>
            <Text size="xs" fw={700} c="grape">Trigger insights</Text>
            <Text size="xs" c="dimmed" mt={2}>
              To exercise the real AI, use <strong>Inject transcript</strong> below — it pushes synthetic speech
              through the live pipeline (trigger → recipe → judge → cooldown). Replay runs a canned scenario
              through the same pipeline, but in a separate demo session:
            </Text>
            {/* MED-1281 — the two `simulate-nudge` buttons that used to sit here were
                removed. They emitted a hand-written Nudge straight onto the socket,
                bypassing trigger selection, the recipe and the judge entirely, so a
                card appearing proved only that the card renders. That made them worse
                than useless for diagnosing AI behaviour: green told you nothing. The
                render/delivery path they nominally covered is already visible in the
                "Automatic reads" rows below, which report outcome = surfaced /
                suppressed_cooldown / suppressed_empty per decision (MED-1245). */}
            <Group gap="xs" mt={8}>
              <Button size="xs" variant="light" color="indigo" loading={busy === 'replay'}
                onClick={() => void replayScenario()}>
                Replay drift scenario
              </Button>
            </Group>
            <Text size="9px" c="dimmed" mt={4}>
              Replay runs a scripted conversation through the real AI pipeline (overwrites session state) — watch
              transcript → inference → nudge stream into the timeline.
            </Text>

            {/* MED-1281 — unlike the simulate buttons above (which emit a
                hand-written nudge straight onto the socket, bypassing the AI),
                injection feeds the real pipeline. Leader-only, mirroring the
                pause/resume-AI gate; the server checks again regardless. */}
            {canInject && (
              <TranscriptInjector
                sessionId={sessionId}
                participants={participants}
                lastCheckAt={lastCheckAt}
              />
            )}
          </Box>

          {/* Controls + filter */}
          <Group gap="xs" justify="space-between">
            <Group gap={6}>
              <Button size="xs" variant="subtle" leftSection={<RefreshCw size={12} />} onClick={() => void loadDispatchTrace()} loading={loading}>
                Refresh
              </Button>
              {canForceJoin && (
                <Button size="xs" color="blue" leftSection={<Zap size={12} />} onClick={() => void forceRejoin()} loading={forcing} data-testid="session-debug-force-join">
                  Force join
                </Button>
              )}
            </Group>
          </Group>
          <Chip.Group multiple value={active} onChange={(v) => setActive(v as Category[])}>
            <Group gap={6}>
              {CATEGORIES.map((c) => (
                <Chip key={c.key} value={c.key} size="xs" variant="outline">{c.label}</Chip>
              ))}
            </Group>
          </Chip.Group>
          {/* Topic anchor — the #1 reason topic-drift nudges silently never fire. */}
          {counts.anchor && (
            <Box
              style={{
                background: counts.anchor.ok ? '#ebfbee' : '#fff5f5',
                border: `1px solid ${counts.anchor.ok ? '#b2f2bb' : '#ffc9c9'}`,
                borderRadius: 8,
                padding: 8,
              }}
            >
              <Text size="xs" fw={700} c={counts.anchor.ok ? 'teal' : 'red'}>
                {counts.anchor.ok ? '✓ Topic anchor present' : '⚠ Topic anchor EMPTY — topic-drift nudges cannot fire'}
              </Text>
              <Text size="xs" c="dimmed">{counts.anchor.detail}</Text>
            </Box>
          )}
          {/* Pipeline health — reads left→right: audio in → AI analyzing → nudges out.
              A zero anywhere upstream of "nudges" tells you where it breaks. */}
          <Text size="xs" c="dimmed" ff="monospace">
            audio {counts.transcript} lines · AI analyses {inferenceStats.analyses} (debounced {inferenceStats.debounced}) · nudges {counts.nudge}
          </Text>

          {/* MED-1210 — Read-the-room diagnostics: why "Generate Recommendation"
              did/didn't surface a prompt. Newest first; each row expands to the
              full request/response/eval. Only the leader who clicked receives
              these (SI emits point-to-point), so the section is empty otherwise. */}
          {readTheRoomTraces && readTheRoomTraces.length > 0 && (
            <Box style={{ background: '#f8fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 10 }}>
              <Text size="xs" fw={700} c="grape">Read-the-room ({readTheRoomTraces.length})</Text>
              <Text size="9px" c="dimmed" mt={2}>
                Each “Generate Recommendation” click and why it did or didn’t surface a prompt.
              </Text>
              <Stack gap={6} mt={8}>
                {[...readTheRoomTraces].reverse().map(t => (
                  <ReadTheRoomTraceRow key={t.traceId} trace={t} />
                ))}
              </Stack>
            </Box>
          )}

          {/* MED-1245 — Automatic reads: why the real-time AI nudged or stayed
              silent each window, and why a would-be nudge did/didn't surface.
              Newest first; each row expands to the model's `why` + message. Only
              the leader receives these (SI emits to the leaders room). */}
          {inferenceDetails && inferenceDetails.length > 0 && (
            <Box style={{ background: '#f8fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 10 }}>
              <Text size="xs" fw={700} c="grape">Automatic reads ({inferenceDetails.length})</Text>
              <Text size="9px" c="dimmed" mt={2}>
                Each real-time AI window and why it did or didn’t surface an insight.
              </Text>
              <Stack gap={6} mt={8}>
                {[...inferenceDetails].reverse().map(d => (
                  // Key by the stable per-emit ts (NOT the reversed index, which
                  // shifts on every append and would remount+collapse open rows).
                  // ts = one ISO stamp per serialized pipeline tick → unique.
                  <InferenceDetailRow key={d.ts} detail={d} />
                ))}
              </Stack>
            </Box>
          )}

          {/* Timeline */}
          <ScrollArea.Autosize mah="62vh" type="auto">
            <Stack gap={5}>
              {timeline.length === 0 && !loading && (
                <Text size="sm" c="dimmed" py="md">
                  No events yet. Click <strong>Force join</strong> to re-dispatch, or use a Trigger button above.
                </Text>
              )}
              {timeline.map((e, i) => (
                <Box
                  key={`${e.ts}-${e.hop}-${i}`}
                  style={{ borderLeft: `3px solid var(--mantine-color-${STATUS_COLOR[e.status]}-6)`, paddingLeft: 8, paddingTop: 2, paddingBottom: 2 }}
                >
                  <Group gap={6} wrap="nowrap" align="center">
                    <Badge size="xs" color={STATUS_COLOR[e.status]} variant="light">{e.status}</Badge>
                    {/* MED-1281 — synthetic speech must never read as something
                        a person actually said in the room. */}
                    {e.data?.injected === true && (
                      <Badge size="xs" color="grape" variant="filled">injected</Badge>
                    )}
                    <Text size="xs" c="dimmed" ff="monospace">{SOURCE_LABEL[e.source]} · {e.hop}</Text>
                    <Text size="xs" c="dimmed" ml="auto">{formatTime(e.ts)}</Text>
                  </Group>
                  <Text size="sm" style={{ wordBreak: 'break-word' }}>{e.detail}</Text>
                </Box>
              ))}
            </Stack>
          </ScrollArea.Autosize>
        </Stack>
      </Drawer>
    </>
  );
}

export default SessionDebugDrawer;
